import { Card, Tooltip, Input, message } from 'antd'
import { EyeInvisibleOutlined, EditOutlined, MailOutlined, LogoutOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import './style.less'
import { ChangeEmailModal } from '../Settings/EmailModal'
import { SessionRevisor } from '../../types/session'
import { Link, navigate } from 'gatsby'
import { ChangePasswordModal } from '../Settings/PasswordModal'
import { httpPostRevisor, httpPutRevisor } from '../../services/http'
import { removeStoredAuthTokenRevisor } from '../../utils/auth-token'

const { Search } = Input
const { Meta } = Card

function Avatar({ name }: { name: string }) {
    return (
        <h3 className="revisor-avatar">
            {name
                .trim()
                .split(' ')
                .slice(0, 2)
                .map((n) => n[0].toUpperCase())
                .join('')}
        </h3>
    )
}

export default function RevisorUserSettings({ session }: { session: SessionRevisor }) {
    const [changeEmail, setChangeEmail] = useState(false)
    const [includeExisting, setIncludeExisting] = useState(false)
    const [changePassword, setChangePassword] = useState(false)
    const [changingName, setChangingName] = useState<boolean>(false)
    const [changingNameLoading, setChangingNameLoading] = useState<boolean>(false)
    const [userName, setUserName] = useState<string>(session?.user?.name)

    const fallbackName = 'Anonymous User'
    const fallbackEmail = 'no@email.com'

    useEffect(() => {
        setUserName(session?.user?.name)
        setIncludeExisting(!!session?.user?.has_password)
    }, [session])

    const updateName = async (value: string) => {
        setChangingNameLoading(true)
        try {
            await httpPutRevisor(`/auth-revisor/change-user-name/${value}`, {})
            setUserName(value)
            message.success('Changed name!')
        } catch (error) {
            // message.error('something went wrong');
        } finally {
            setChangingName(false)
            setChangingNameLoading(false)
        }
    }

    async function signOut() {
        removeStoredAuthTokenRevisor()
        httpPostRevisor('/auth-revisor/signout')
        navigate('/')
    }

    return (
        <div>
            <Card>
                <Card
                    actions={[
                        <Tooltip title="Change E-mail">
                            <MailOutlined
                                onClick={() => {
                                    setChangeEmail(true)
                                }}
                                key="email"
                            />
                        </Tooltip>,
                        <Tooltip title="Change password">
                            <EyeInvisibleOutlined
                                onClick={() => {
                                    setChangePassword(true)
                                }}
                                key="password"
                            />
                        </Tooltip>,
                        <Tooltip title="Sign out">
                            <LogoutOutlined
                                onClick={() => {
                                    signOut()
                                }}
                                key="logout"
                            />
                        </Tooltip>,
                        <div key="ellipsis" />,
                    ]}
                >
                    <Meta
                        avatar={<Avatar name={userName ?? fallbackName} />}
                        title={
                            (changingName && (
                                <Search
                                    placeholder={userName || 'Enter your name'}
                                    className="ll-period-config-title"
                                    allowClear
                                    loading={changingNameLoading}
                                    enterButton={<EditOutlined />}
                                    onSearch={updateName}
                                />
                            )) || (
                                <div onClick={() => setChangingName(true)}>
                                    <Tooltip title="Click to change">
                                        {userName ?? fallbackName}
                                    </Tooltip>
                                    {!(
                                        userName ||
                                        session?.user?.is_mail_verified ||
                                        session?.user?.email
                                    ) && (
                                        <span style={{ fontSize: 12 }}>
                                            {' '}
                                            - Already have an account?{' '}
                                            <Link
                                                to="/login-revisor/"
                                                style={{ textDecoration: 'underline' }}
                                            >
                                                Login{' '}
                                            </Link>
                                        </span>
                                    )}
                                </div>
                            )
                        }
                        description={session?.user?.email ?? fallbackEmail}
                    />
                </Card>
            </Card>
            <ChangeEmailModal
                visible={changeEmail}
                setVisible={setChangeEmail}
                session={session}
                submitRequest={(values) => httpPutRevisor('/auth-revisor/change-email', values)}
            />
            <ChangePasswordModal
                visible={changePassword}
                setVisible={setChangePassword}
                submitRequest={async (values) => {
                    const res = await httpPutRevisor(
                        includeExisting
                            ? '/auth-revisor/change-password'
                            : '/auth-revisor/set-password',
                        values
                    )
                    if (res.ok) {
                        setIncludeExisting(true)
                    }
                    return res
                }}
                includeExisting={includeExisting}
            />
        </div>
    )
}
